import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';
import type { SelectorOption } from '../common';

interface UseGenerateLogoPromptParams {
    shortDescription: string;
    tagLine: string;
    businessName: string;
    industry: string;
    businessDescription: string;
    maxGenerations: number;
    culture: SelectorOption;
    setShortDescription: (shortDescription: string) => void;
    setTagLine: (tagLine: string) => void;
    setBusinessName: (businessName: string) => void;
    setIndustry: (industry: string) => void;
    setBusinessDescription: (businessDescription: string) => void;
    setMaxGenerations: (maxGenerations: number) => void;
    setCulture: (culture: SelectorOption) => void;
}

export const useGenerateLogoPromptParams = (): UseGenerateLogoPromptParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();
    const shortDescriptionParam = searchParams.get('shortDescription');
    const tagLineParam = searchParams.get('tagLine');
    const businessNameParam = searchParams.get('businessName');
    const industryParam = searchParams.get('industry');
    const businessDescriptionParam = searchParams.get('businessDescription');
    const maxGenerationsParam = searchParams.get('maxGenerations');
    const cultureParam = searchParams.get('culture');
    const [culture, setCulture] = useState<SelectorOption>(
        cultureParam ? { label: cultureParam, value: cultureParam } : { label: 'en-us', value: 'en-us' },
    );
    const [shortDescription, setShortDescription] = useState<string>(shortDescriptionParam ?? '');
    const [tagLine, setTagLine] = useState<string>(tagLineParam ?? '');
    const [businessName, setBusinessName] = useState<string>(businessNameParam ?? '');
    const [industry, setIndustry] = useState<string>(industryParam ?? '');
    const [businessDescription, setBusinessDescription] = useState<string>(businessDescriptionParam ?? '');
    const [maxGenerations, setMaxGenerations] = useState<number>(
        maxGenerationsParam ? parseInt(maxGenerationsParam) : 2,
    );

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string | string[]> = {};

            if (shortDescription) {
                searchParamsObj['shortDescription'] = shortDescription;
            }
            if (tagLine) {
                searchParamsObj['tagLine'] = tagLine;
            }
            if (businessName) {
                searchParamsObj['businessName'] = businessName;
            }
            if (industry) {
                searchParamsObj['industry'] = industry;
            }
            if (businessDescription) {
                searchParamsObj['businessDescription'] = businessDescription;
            }
            if (maxGenerations) {
                searchParamsObj['maxGenerations'] = maxGenerations.toString();
            }
            if (culture) {
                searchParamsObj['culture'] = culture.value;
            }
            setSearchParams(searchParamsObj);
        },
        [
            shortDescription,
            tagLine,
            businessName,
            industry,
            businessDescription,
            maxGenerations,
            culture,
            setSearchParams,
        ],
        500,
    );

    return {
        shortDescription,
        tagLine,
        businessName,
        industry,
        businessDescription,
        maxGenerations,
        culture,
        setShortDescription,
        setTagLine,
        setBusinessName,
        setIndustry,
        setBusinessDescription,
        setMaxGenerations,
        setCulture,
    };
};
